import { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import get from 'lodash/get';

import { Label } from 'components/Typography';

import {
  StyledTextArea,
  CharCount,
  Optional,
  ErrorMessage,
  CheckIcon,
  StyledTooltip,
  Comment
} from './styled';

const TextArea = ({
  name,
  placeholder,
  minHeight,
  formik,
  value,
  onChange,
  className,
  label = '',
  variant = 'dark',
  spacing = 'normal',
  comment,
  optional,
  tooltip,
  textAlign = 'right'
}) => {
  const [characters, setCharacters] = useState(1000);

  const getInputProps = () => {
    const rest = formik?.getFieldProps(name);

    if (!rest) {
      return {
        placeholder,
        value
      };
    }

    return {
      placeholder,
      ...rest
    };
  };

  const handleChange = (e) => {
    if (e.target.value.length > 1000) return;

    if (formik) {
      formik.handleChange(e);
    } else {
      onChange(e);
    }
  };

  useEffect(() => {
    if (formik) {
      const length = formik.values[name]?.length ?? 0;
      setCharacters(1000 - length);
    } else {
      setCharacters(1000 - value.length);
    }
  }, [formik, name, value]);

  const renderLabel = (
    <Label htmlFor={name} variant={variant} spacing={spacing}>
      {label}
      {tooltip && (
        <StyledTooltip variant={variant === 'block' ? 'light' : variant}>
          {tooltip}
        </StyledTooltip>
      )}
      {optional && <Optional>- optional</Optional>}
      {formik && !optional && (
        <>
          {get(formik.touched, name) && get(formik.errors, name) ? (
            <ErrorMessage>{get(formik.errors, name)}</ErrorMessage>
          ) : null}
          {!get(formik.errors, name) && get(formik.touched, name) && formik.dirty && (
            <CheckIcon />
          )}
        </>
      )}
      {comment && <Comment>{comment}</Comment>}
    </Label>
  );

  return (
    <div className={className}>
      {label && renderLabel}
      <StyledTextArea
        minHeight={minHeight || 80}
        {...getInputProps()}
        onChange={handleChange}
      />
      <CharCount
        textAlign={textAlign}
        fontSize="12px"
        opacity="0.5"
        mr="3px"
        variant={variant}
      >
        {characters} characters left
      </CharCount>
    </div>
  );
};

TextArea.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  minHeight: PropTypes.number,
  formik: PropTypes.object,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  comment: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  variant: PropTypes.string,
  spacing: PropTypes.string,
  tooltip: PropTypes.string,
  optional: PropTypes.bool,
  textAlign: PropTypes.string
};

export default TextArea;
